import { useEffect } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Trans } from '@lingui/react/macro';
import { useLingui } from '@lingui/react';
import { msg } from '@lingui/core/macro';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { DatePicker } from '@/components/ui/date-picker';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  getPauseSchema,
  parseDateInputAsLocalEndOfDay,
  type PauseFormData,
} from '@/schemas/dunningWorkflowActions';
import type { billing_PauseRequest } from '@/api/models/billing_PauseRequest';
import { formatLocalDate, parseDateString } from '@/utils/dateUtils';

interface Props {
  open: boolean;
  householdName: string;
  onClose: () => void;
  onConfirm: (req: billing_PauseRequest) => void;
  loading?: boolean;
}

export function PauseModal({ open, householdName, onClose, onConfirm, loading }: Props) {
  const { i18n } = useLingui();

  const form = useForm<PauseFormData>({
    resolver: zodResolver(getPauseSchema(i18n)),
    defaultValues: { mode: 'indefinite', until_date: '', reason: '' },
    mode: 'onSubmit',
  });

  useEffect(() => {
    if (open) {
      form.reset({ mode: 'indefinite', until_date: '', reason: '' });
    }
  }, [open, form]);

  const mode = useWatch({ control: form.control, name: 'mode' });
  const reasonValue = useWatch({ control: form.control, name: 'reason' }) ?? '';

  const onSubmit = (data: PauseFormData) => {
    if (data.mode === 'indefinite') {
      onConfirm({ reason: data.reason.trim(), indefinite: true });
      return;
    }
    const dt = parseDateInputAsLocalEndOfDay(data.until_date);
    if (!dt) return;
    onConfirm({
      reason: data.reason.trim(),
      indefinite: false,
      paused_until: dt.toISOString(),
    });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            <Trans>Pause dunning</Trans>
          </DialogTitle>
          <DialogDescription>
            {i18n._(
              msg({
                id: 'dunning.pause.description',
                message: 'Stop dunning emails for {householdName}.',
              }),
              { householdName },
            )}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="mode"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    <Trans>Pause mode</Trans>
                  </FormLabel>
                  <RadioGroup
                    name={field.name}
                    value={field.value}
                    onValueChange={field.onChange}
                    aria-label={i18n._(msg({ id: 'dunning.pause.mode.aria-label', message: 'Pause mode' }))}
                  >
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="indefinite" id="pause-indefinite" />
                      <Label htmlFor="pause-indefinite"><Trans>Indefinitely</Trans></Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="until_date" id="pause-until" />
                      <Label htmlFor="pause-until"><Trans>Until a specific date</Trans></Label>
                    </div>
                  </RadioGroup>
                  <FormMessage />
                </FormItem>
              )}
            />

            {mode === 'until_date' && (
              <FormField
                control={form.control}
                name="until_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      <Trans>Pause until</Trans>
                    </FormLabel>
                    <FormControl>
                      <DatePicker
                        ref={field.ref}
                        value={parseDateString(field.value)}
                        onChange={(date) => field.onChange(formatLocalDate(date))}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <FormField
              control={form.control}
              name="reason"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    <Trans>Reason</Trans>
                  </FormLabel>
                  <FormControl>
                    <Textarea
                      maxLength={500}
                      aria-required="true"
                      placeholder={i18n._(msg({ id: 'dunning.pause.reason.placeholder', message: 'Parent in dispute about December invoice…' }))}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                  <div className="text-xs text-muted-foreground text-right">
                    {reasonValue.length}/500
                  </div>
                </FormItem>
              )}
            />

            <DialogFooter className="mt-4">
              <Button type="button" variant="ghost" onClick={onClose} disabled={loading}>
                <Trans>Cancel</Trans>
              </Button>
              <Button type="submit" disabled={loading}>
                {i18n._(
                  msg({
                    id: 'dunning.pause.confirm.button',
                    message: 'Pause dunning for {householdName}',
                  }),
                  { householdName },
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
